import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Flex, Heading, Icon, Spacer, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
} from 'date-fns';
import { Tooltip } from '@/components/ui/tooltip';
import { PillGroup } from '@/components/ui/pill-group';
import { toaster } from '@/components/ui/toaster';
import usePlannerPlansStore from '@/utils/use-planner-plans';
import WeekStrip from './week-strip';
import MonthGrid from './month-grid';

type CalendarView = 'week' | 'month';

interface CalendarStripCardProps {
  workspaceId: string;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

// Padded 42-day window used to keep the month grid always fully covered by
// loaded data (6 weeks x 7 days, starting from the first grid cell).
function monthPaddedRange(anchor: Date): { from: string; to: string } {
  const gridStart = startOfWeek(startOfMonth(anchor));
  const gridEnd = endOfWeek(endOfMonth(anchor));
  return { from: format(gridStart, 'yyyy-MM-dd'), to: format(gridEnd, 'yyyy-MM-dd') };
}

function weekRange(anchor: Date): { from: string; to: string } {
  return { from: format(startOfWeek(anchor), 'yyyy-MM-dd'), to: format(endOfWeek(anchor), 'yyyy-MM-dd') };
}

export default function CalendarStripCard({
  workspaceId,
  selectedDate,
  onSelectDate,
}: CalendarStripCardProps) {
  const [view, setView] = useState<CalendarView>('week');
  const [anchor, setAnchor] = useState<Date>(new Date());

  const items = usePlannerPlansStore((s) => s.items);
  const error = usePlannerPlansStore((s) => s.error);
  const loadRange = usePlannerPlansStore((s) => s.loadRange);

  const plansByDate = useMemo(() => {
    const map: Record<string, (typeof items)[number][]> = {};
    for (const plan of items) {
      (map[plan.plan_date.substring(0, 10)] ??= []).push(plan);
    }
    // Workspace plan first — day cells render its dot/chip ahead of "My
    // Plan"'s, matching the scope tab strip's order.
    for (const date of Object.keys(map)) {
      map[date].sort((a, b) => (a.scope === b.scope ? 0 : a.scope === 'workspace' ? -1 : 1));
    }
    return map;
  }, [items]);

  const range = useMemo(
    () => (view === 'week' ? weekRange(anchor) : monthPaddedRange(anchor)),
    [view, anchor],
  );

  useEffect(() => {
    if (!workspaceId) return;
    loadRange(workspaceId, range.from, range.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, range.from, range.to]);

  useEffect(() => {
    if (error) {
      toaster.create({ type: 'error', title: error });
    }
  }, [error]);

  const handlePrev = useCallback(() => {
    setAnchor((current) => (view === 'week' ? subWeeks(current, 1) : subMonths(current, 1)));
  }, [view]);

  const handleNext = useCallback(() => {
    setAnchor((current) => (view === 'week' ? addWeeks(current, 1) : addMonths(current, 1)));
  }, [view]);

  const handleToday = useCallback(() => {
    setAnchor(new Date());
  }, []);

  // Period label with the year set apart beneath it ("JUN 14 – 20" / "2026"),
  // per the prototype — the year rarely changes, so it stays out of the way.
  const label = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(anchor);
      const end = endOfWeek(anchor);
      const sameMonth = format(start, 'MMM') === format(end, 'MMM');
      return sameMonth
        ? `${format(start, 'MMM d')} – ${format(end, 'd')}`
        : `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
    }
    return format(anchor, 'MMMM');
  }, [view, anchor]);

  return (
    <Card.Root className='no-print'>
      <Card.Body>
        <Flex
          mb={4}
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'start', md: 'center' }}
          gap={3}
        >
          <Flex align='center' gap={3}>
            <Flex gap={1.5}>
              <Tooltip content={view === 'week' ? 'Previous week' : 'Previous month'}>
                <Button
                  onClick={handlePrev}
                  variant='outline'
                  size='xs'
                  aria-label={view === 'week' ? 'Previous week' : 'Previous month'}
                >
                  <Icon as={ChevronLeftIcon} w={4} h={4} />
                </Button>
              </Tooltip>
              <Tooltip content={view === 'week' ? 'Next week' : 'Next month'}>
                <Button
                  onClick={handleNext}
                  variant='outline'
                  size='xs'
                  aria-label={view === 'week' ? 'Next week' : 'Next month'}
                >
                  <Icon as={ChevronRightIcon} w={4} h={4} />
                </Button>
              </Tooltip>
            </Flex>
            <Box>
              <Heading as='h2' fontFamily='heading' fontWeight='800' fontSize='h2' textTransform='uppercase'>
                {label}
              </Heading>
              <Text fontSize='sm' color='fg.muted' lineHeight='short'>
                {format(anchor, 'yyyy')}
              </Text>
            </Box>
          </Flex>

          <Spacer />

          <Flex align='center' gap={2}>
            <Button onClick={handleToday} variant='outline' size='xs'>
              Today
            </Button>
            <PillGroup
              size='sm'
              value={view}
              onChange={setView}
              items={[
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
              ]}
            />
          </Flex>
        </Flex>

        {view === 'week' ? (
          <WeekStrip
            anchor={anchor}
            selectedDate={selectedDate}
            plansByDate={plansByDate}
            onSelectDate={onSelectDate}
          />
        ) : (
          <MonthGrid
            anchor={anchor}
            selectedDate={selectedDate}
            plansByDate={plansByDate}
            onSelectDate={onSelectDate}
          />
        )}
      </Card.Body>
    </Card.Root>
  );
}
